var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import Collector from "./Collector";
import { CollectorSetupResultStatus } from "./collector-setup-result";
import { CollectorId } from "./index";
import utils, { isMobileUserAgent } from "../utils/index";
import { PlasmIntegrationError } from "../errrors/PlasmIntegrationError";
import { PlasmInternalError } from "../errrors/PlasmInternalError";
import { Sentry } from "../utils/Sentry";
class ImageCollector extends Collector {
    constructor() {
        super(...arguments);
        this.drawAndSendImage = (data) => __awaiter(this, void 0, void 0, function* () {
            const image = yield this.drawImage();
            if (image != null) {
                yield this.sendImage(image, data);
            }
        });
        this.drawImageFromMobileApp = () => __awaiter(this, void 0, void 0, function* () {
            const captureStream = yield window.flutter_inappwebview.callHandler("captureStream");
            if (captureStream.length === 0) {
                return null;
            }
            const fullImage = `data:image/jpeg;base64,${captureStream}`;
            const img = new Image();
            return new Promise((resolve) => {
                img.onload = () => resolve(img);
                img.onerror = (error) => {
                    Sentry.captureError(error);
                    resolve(null);
                };
                img.src = fullImage;
            });
        });
        this.drawImage = () => __awaiter(this, void 0, void 0, function* () {
            const { width, height } = this.IMAGE_DIMENSIONS;
            const quality = Number(this.IMAGE_QUALITY) / 100;
            let sourceImage;
            let sourceWidth;
            let sourceHeight;
            if (utils.isAccessedFromMobileApp() && this.id === CollectorId.screen) {
                const mobileAppImage = yield this.drawImageFromMobileApp();
                if (mobileAppImage == null) {
                    return null;
                }
                sourceImage = mobileAppImage;
                sourceWidth = mobileAppImage.width;
                sourceHeight = mobileAppImage.height;
            }
            else {
                const { videoWidth, videoHeight } = this.video;
                sourceImage = this.video;
                sourceWidth = videoWidth;
                sourceHeight = videoHeight;
            }
            const scale = Math.min(width / sourceWidth, height / sourceHeight);
            const destinationWidth = sourceWidth * scale;
            const destinationHeight = sourceHeight * scale;
            this.canvas.width = destinationWidth;
            this.canvas.height = destinationHeight;
            this.context.clearRect(0, 0, destinationWidth, destinationHeight);
            this.context.drawImage(sourceImage, 0, 0, destinationWidth, destinationHeight);
            return new Promise((resolve) => {
                this.canvas.toBlob((blob) => {
                    if (blob == null) {
                        resolve(null);
                        return;
                    }
                    return blob.arrayBuffer().then(resolve);
                }, "image/jpeg", quality);
            });
        });
        this.stopInner = () => __awaiter(this, void 0, void 0, function* () {
            if (this.timerId != null) {
                clearTimeout(this.timerId);
            }
            if (this.stream != null) {
                this.stream.getTracks().forEach((track) => track.stop());
            }
        });
    }
    validateOptions(options) {
        const { snapshotsMinInterval, screenshotsMinInterval } = this.plasm.config;
        if (this.id === CollectorId.camera && options.interval < Number(snapshotsMinInterval)) {
            throw new PlasmIntegrationError({
                msg: `snapshots "interval" should greater than or equal ${snapshotsMinInterval}`,
            });
        }
        if (this.id === CollectorId.screen && options.interval < Number(screenshotsMinInterval)) {
            throw new PlasmIntegrationError({
                msg: `screenshots "interval" should greater than or equal ${screenshotsMinInterval}`,
            });
        }
    }
    setupCollector() {
        return __awaiter(this, void 0, void 0, function* () {
            this.setupDomElements();
            try {
                yield this.setupStream();
            }
            catch (err) {
                switch (err.name) {
                    case "NotFoundError":
                        return this.buildSetupResult(CollectorSetupResultStatus.device_not_found);
                    case "NotAllowedError":
                        return this.buildSetupResult(CollectorSetupResultStatus.permission_denied);
                    case "NotReadableError":
                        return this.buildSetupResult(CollectorSetupResultStatus.device_in_use);
                    default:
                        throw err;
                }
            }
            return this.buildSetupResult(CollectorSetupResultStatus.success);
        });
    }
    setupDomElements() {
        this.video = document.createElement("video");
        this.video.setAttribute("playsinline", "");
        this.canvas = document.createElement("canvas");
        const context = this.canvas.getContext("2d");
        if (context == null) {
            throw new PlasmInternalError({ msg: "canvas context cannot be created" });
        }
        this.context = context;
    }
    getReady() {
        return __awaiter(this, void 0, void 0, function* () {
            const isScreenOnMobileDevice = this.id === CollectorId.screen && utils.isAccessedFromMobileApp();
            if (!isScreenOnMobileDevice) {
                yield this.video.play();
            }
        });
    }
    start() {
        return __awaiter(this, void 0, void 0, function* () {
            this.drawAndSendImage({ trigger: "interval" });
            this.startCollecting();
        });
    }
    startCollecting() {
        if (this.timerId != null) {
            clearTimeout(this.timerId);
        }
        this.timerId = setInterval(() => this.drawAndSendImage({ trigger: "interval" }), this.options.interval);
    }
    generateExtraData() {
        return __awaiter(this, void 0, void 0, function* () {
            const isTakenFromMobile = isMobileUserAgent();
            const { shotsExtraDataGenerator } = this.plasm.options;
            let extraData = { isTakenFromMobile };
            if (shotsExtraDataGenerator != null) {
                const type = this.id;
                const generatedShotsExtraData = yield shotsExtraDataGenerator({ type });
                if (generatedShotsExtraData != null) {
                    extraData = Object.assign(Object.assign({}, extraData), generatedShotsExtraData);
                }
            }
            return JSON.stringify(extraData);
        });
    }
}
export default ImageCollector;
